import { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { MdClose } from "react-icons/md";
import {
    ActionSection,
    FieldWrappers,
    InputGroup,
    Label,
    ModalHeader,
    ScrollableContent,
    SideCancelBtn,
    SubmitBtn,
    Wrapper,
} from "../Styles/InputStyles";
import CategoryCard from "../CategoryCard";
import SelectItemModal from "../SelectItemModal";
import { capitalizeFirstLetter } from "../../helpers";

const EditCommission = ({
    selectedData,
    editCommissionLoading,
    handleClose,
    editCommissionMutate,
    tenantId,
    staffList
}) => {
    const [staffModalOpen, setStaffModalOpen] = useState(false);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [height, setHeight] = useState(window.innerHeight);

    useEffect(() => {
        if (selectedData) {
            const fullStaffDetails = staffList.find(
                staff => staff.id === selectedData.staffId
            );


            if (fullStaffDetails) {
                setSelectedStaff({
                    ...fullStaffDetails,
                    balance: selectedData.balance,
                    commissionId: selectedData.id
                });
            } else {

                setSelectedStaff(selectedData);
            }
        } else {
            setSelectedStaff(null);
        }
    }, [selectedData, staffList]);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const handleStaffSelect = (staff, setFieldValue) => {
        if (staff) {
            if (selectedData && selectedData.staffId === staff.id) {
                setSelectedStaff({
                    ...staff,
                    balance: selectedData.balance,
                    commissionId: selectedData.id
                });
                setFieldValue(`commission_${staff.id}`, selectedData.balance);
            } else {
                setSelectedStaff(staff);
                setFieldValue(`commission_${staff.id}`, "");
            }
            setStaffModalOpen(false);
        } else {
            setSelectedStaff(null);
        }
    };

    const renderSelectedStaff = (staff) => {
        if (!staff) return "Select Staff";

        return (
            <div className="flex text-left">
                <span>{capitalizeFirstLetter(staff.staffName || staff.name)}</span>
                {staff.mobile && (
                    <span className="ml-1 text-gray-500">({staff.mobile})</span>
                )}
            </div>
        );
    };

    const generateValidationSchema = (selectedStaff) => {
        const shape = {};

        if (selectedStaff) {
            const staffId = selectedStaff.id;
            shape[`commission_${staffId}`] = Yup.number()
                .typeError("Commission must be a number")
                .min(1, "Commission must be greater than 0")
                .max(500000, "Commission cannot be more than ₹5,00,000")
                .required(`Commission for ${selectedStaff.staffName || selectedStaff.name} is required`);
        }

        return Yup.object(shape);
    };

    const generateInitialValues = (selectedStaff) => {
        const values = {};

        if (selectedStaff) {
            const staffId = selectedStaff.id;
            const commissionAmount = selectedStaff.balance || "";
            values[`commission_${staffId}`] = commissionAmount;
        }

        return values;
    };

    const handleSubmit = (values, { resetForm }) => {
        if (selectedStaff) {
            const commissionData = {
                staffId: selectedStaff.id,
                amount: parseFloat(values[`commission_${selectedStaff.id}`]) || 0,
                id: selectedData?.id,
            };
            editCommissionMutate(commissionData);
        }
    };

    return (
        <Container
            style={{
                height: `${height}px`,
                overflow: "auto",
                WebkitOverflowScrolling: "touch",
            }}
        >
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Edit Commission</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <div className="px-6 flex flex-col justify-between">
                <Formik
                    initialValues={generateInitialValues(selectedStaff)}
                    validationSchema={generateValidationSchema(selectedStaff)}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {({ values, setFieldValue, resetForm, errors, touched }) => {
                        return (
                            <Form>
                                <ScrollableContent className="max-h-[calc(100dvh-120px)]">
                                    <Wrapper>
                                        <InputGroup>
                                            <Label>Select Staff *</Label>
                                            <CategoryCard
                                                title="Staff"
                                                selectedCategory={
                                                    selectedStaff
                                                        ? {
                                                            label: renderSelectedStaff(selectedStaff),
                                                            items: [selectedStaff],
                                                        }
                                                        : null
                                                }
                                                onClick={() => setStaffModalOpen(true)}
                                            />
                                            {!selectedStaff && (
                                                <div className="text-red-500 text-sm mt-1">
                                                    Staff is required
                                                </div>
                                            )}
                                        </InputGroup>

                                        {selectedStaff && (
                                            <InputGroup key={selectedStaff.id}>
                                                <Label htmlFor={`commission_${selectedStaff.id}`}>
                                                    Commission for {capitalizeFirstLetter(selectedStaff.staffName || selectedStaff.name)}
                                                    {/* {selectedStaff.mobile && (
                                                        <span className="text-gray-500 text-sm ml-2">
                                                            (#{selectedStaff.mobile})
                                                        </span>
                                                    )} */}
                                                </Label>
                                                <FieldWrappers>
                                                    <Field
                                                        name={`commission_${selectedStaff.id}`}
                                                        type="number"
                                                        // step="0.01"
                                                        className="p-2 rounded w-full"
                                                        placeholder={`Enter commission amount for ${capitalizeFirstLetter(selectedStaff.staffName || selectedStaff.name)}`}
                                                    />
                                                </FieldWrappers>
                                                {errors[`commission_${selectedStaff.id}`] &&
                                                    touched[`commission_${selectedStaff.id}`] && (
                                                        <div className="text-red-500 text-sm mt-1">
                                                            {errors[`commission_${selectedStaff.id}`]}
                                                        </div>
                                                    )}
                                            </InputGroup>
                                        )}
                                    </Wrapper>
                                </ScrollableContent>

                                <ActionSection>
                                    <SideCancelBtn
                                        type="button"
                                        onClick={() => {
                                            resetForm();
                                            setSelectedStaff(selectedData ? {
                                                ...selectedData,
                                                id: selectedData.staffId
                                            } : null);
                                            handleClose();
                                        }}
                                    >
                                        Cancel
                                    </SideCancelBtn>
                                    <SubmitBtn
                                        type="submit"
                                        disabled={editCommissionLoading || !selectedStaff}
                                    >
                                        {editCommissionLoading ? "Updating..." : "Update"}
                                    </SubmitBtn>
                                </ActionSection>

                                <SelectItemModal
                                    isOpen={staffModalOpen}
                                    onClose={() => setStaffModalOpen(false)}
                                    onSelect={(staff) => handleStaffSelect(staff, setFieldValue)}
                                    options={staffList}
                                    selectedItem={selectedStaff}
                                    title="Select Staff"
                                    searchPlaceholder="Search staffs..."
                                    idKey="id"
                                    nameKey="name"
                                    descriptionKeys={["mobile"]}
                                    emptyMessage="No staff available"
                                    noResultsMessage="No staff found"
                                    showClearSelection={true}
                                    enableSearch={true}
                                />
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`;

export default EditCommission;
